import React, { FC, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import PageWrapper from "@components/Home/PageWrapper";

export interface GameScreenShellProps {
  gameName: string;
  level: number;
  progress: number;
  feedback: string | null;
  timerSeconds: number | null;
  score: number | null;
  paused?: boolean;
  onBack: () => void;
  onPause?: () => void;
  /** When set, show "Game X of Y" (guided session) */
  gameIndex?: number;
  totalGames?: number;
  /** When set, show mute toggle (spec: global mute in session UI) */
  muted?: boolean;
  onMuteChange?: (muted: boolean) => void;
}

const GameScreenShell: FC<PropsWithChildren<GameScreenShellProps>> = ({
  gameName,
  level,
  progress,
  feedback,
  timerSeconds,
  score,
  paused = false,
  onBack,
  onPause,
  gameIndex,
  totalGames,
  muted = false,
  onMuteChange,
  children,
}) => {
  const { t } = useTranslation();
  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };
  const showGameOfTotal =
    gameIndex != null && totalGames != null && totalGames > 1;

  return (
    <PageWrapper>
      {/* Header: Back | Game Name | Level / Game X of Y */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={1}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ minHeight: 48 }}
        >
          {t("games.shell.back")}
        </Button>
        <Typography variant="subtitle1" fontWeight={600}>
          {gameName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {showGameOfTotal
            ? t("games.shell.gameOfTotal", {
                current: gameIndex + 1,
                total: totalGames,
              })
            : t("games.shell.level", { level })}
        </Typography>
      </Box>

      {/* Progress bar */}
      <Box sx={{ mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, progress * 100)}
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>

      {/* Game + feedback + footer grouped and vertically centered */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        minHeight={0}
      >
        <Box display="flex" flexDirection="column" margin="auto 0">
          {/* Game area - fixed min height to prevent jump when content changes */}
          <Box
            minHeight={360}
            display="flex"
            flexDirection="column"
            sx={{ flexShrink: 0 }}
          >
            {children}
          </Box>

          {/* Feedback zone - fixed height so layout doesn't shift */}
          <Box
            height={48}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            sx={{ borderTop: 1, borderColor: "divider" }}
          >
            {feedback ? (
              <Typography
                variant="body1"
                fontWeight={500}
                color={feedback.startsWith("Correct") || feedback.includes("!") ? "primary.main" : "text.secondary"}
              >
                {feedback}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                &nbsp;
              </Typography>
            )}
          </Box>

          {/* Footer: Timer | Score | Mute | Pause - fixed height so layout doesn't shift */}
          <Box
            height={56}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={1}
            flexShrink={0}
            sx={{ borderTop: 1, borderColor: "divider" }}
          >
            <Typography variant="body2" color="text.secondary">
              {timerSeconds != null ? formatTime(timerSeconds) : "—"}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {score != null ? t("games.shell.scoreValue", { count: score }) : "—"}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              {onMuteChange != null ? (
                <IconButton
                  onClick={() => onMuteChange(!muted)}
                  aria-label={muted ? t("games.shell.unmute") : t("games.shell.mute")}
                  sx={{ minWidth: 48, minHeight: 48 }}
                >
                  {muted ? (
                    <VolumeOffIcon fontSize="small" />
                  ) : (
                    <VolumeUpIcon fontSize="small" />
                  )}
                </IconButton>
              ) : null}
              {onPause ? (
                <IconButton
                  onClick={onPause}
                  aria-label={paused ? t("games.shell.resume") : t("games.shell.pause")}
                  sx={{ minWidth: 48, minHeight: 48 }}
                >
                  {paused ? (
                    <PlayArrowIcon fontSize="small" />
                  ) : (
                    <PauseIcon fontSize="small" />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ width: 48 }} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
};

export default GameScreenShell;
